<template>
  <div class="cancel-flow">
    <!-- Terminal confirmation replaces the whole flow -->
    <FlowConfirmation
      v-if="terminal === 'cancelled'"
      title="Until next time!"
      body-prefix="Your PRO+ access will remain until "
      :bold="expiryShort || ''"
      body-suffix=". After that, you'll switch to the free plan."
      @continue="$emit('done')"
    />
    <FlowConfirmation
      v-else-if="terminal === 'paused'"
      title="See you soon!"
      body-prefix="You have paused your subscription until "
      :bold="resumesOn"
      body-suffix="."
      @continue="$emit('done')"
    />

    <template v-else>
      <button type="button" class="btn btn-link px-0 mb-2 text-decoration-none" @click="back">
        <span class="material-icons align-middle">chevron_left</span> Back
      </button>

      <ProgressBar :current="currentStep" :total="totalSteps" />

      <div v-if="reminderToast" class="alert alert-info py-2">Reminder scheduled — confirmation coming soon</div>

      <WhatYoullLose v-if="stepKind === 'lose'" />
      <AccidentalRenewal v-else-if="stepKind === 'renewal'" :reminder-on="reminderOn || ''" />
      <ReasonPicker
        v-else-if="stepKind === 'reason'"
        v-model:selected="selectedReason"
        v-model:otherText="otherText"
        @not-working-selected="showSupportModal = true"
      />
      <PauseDurationChips v-else-if="stepKind === 'pause'" v-model="pauseMonths" />
      <FinalConfirmation v-else-if="stepKind === 'final'" :expiry-on="expiryShort" />

      <div v-if="error" class="alert alert-danger mt-3">{{ error }}</div>

      <!-- Persistent bottom button bar -->
      <div class="cancel-flow__actions mt-4">
        <template v-if="stepKind === 'final'">
          <button type="button" class="btn btn-primary w-100 mb-2" @click="$emit('close')">
            Keep my subscription
          </button>
          <button type="button" class="btn btn-danger w-100" :disabled="submitting" @click="doCancel">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            Cancel PRO+ subscription
          </button>
        </template>
        <template v-else>
          <button type="button" class="btn btn-primary w-100" :disabled="submitting" @click="onPrimary">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            {{ primaryLabel }}
          </button>
          <button
            v-if="linkLabel"
            type="button"
            class="btn btn-link w-100 mt-2 text-decoration-underline"
            @click="advance"
          >
            {{ linkLabel }}
          </button>
        </template>
      </div>
    </template>

    <NotWorkingSupportModal
      v-if="showSupportModal"
      @back="showSupportModal = false"
      @contact="contactSupportFromModal"
      @continue="continueFromSupport"
    />
    <StoreRedirectModal
      v-if="storeRedirectBody"
      :body-text="storeRedirectBody"
      :confirm-label="cancelNeedsSupport ? 'Contact support' : 'Go'"
      @cancel="closeStoreRedirect"
      @go="confirmStoreRedirect"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import graphClient from "@/utils/graphql/graph_client";
import { CancelWebSubscription, PauseWebSubscription } from "@/utils/graphql/subscriptions";
import { errorMessage } from "../../errorMessage";
import { contactSupport } from "../../support";
import {
  cancelPrompt,
  cancelReasonPayload,
  manageAction,
  pauseDurationEnum,
  skipsPauseStep,
  longOrdinalDate,
  shortDate,
  reminderDate,
  resumeDate,
  type SubscriptionSource,
  type CancelReasonSlug,
} from "../../subscriptionLogic";
import FlowConfirmation from "../FlowConfirmation.vue";
import PauseDurationChips from "../PauseDurationChips.vue";
import StoreRedirectModal from "../StoreRedirectModal.vue";
import AccidentalRenewal from "./AccidentalRenewal.vue";
import FinalConfirmation from "./FinalConfirmation.vue";
import NotWorkingSupportModal from "./NotWorkingSupportModal.vue";
import ProgressBar from "./ProgressBar.vue";
import ReasonPicker from "./ReasonPicker.vue";
import WhatYoullLose from "./WhatYoullLose.vue";

const props = defineProps<{
  source: SubscriptionSource;
  expirationDate: string | null;
  managementUrl: string | null;
  storeLabel: string;
}>();

const emit = defineEmits<{ (e: "close"): void; (e: "done"): void }>();

const currentStep = ref(0);
const selectedReason = ref<CancelReasonSlug | null>(null);
const otherText = ref("");
const pauseMonths = ref(1);
const submitting = ref(false);
const error = ref("");
const terminal = ref<null | "cancelled" | "paused">(null);
const resumesOn = ref("");
const showSupportModal = ref(false);
const storeRedirectBody = ref("");
const cancelNeedsSupport = ref(false);
const reminderToast = ref(false);

const skipsPause = computed(() => skipsPauseStep(props.source));
const totalSteps = computed(() => (skipsPause.value ? 4 : 5));

const expiryShort = computed(() => shortDate(props.expirationDate));
const reminderOn = computed(() => longOrdinalDate(reminderDate(props.expirationDate)));

type StepKind = "lose" | "renewal" | "reason" | "pause" | "final";
const stepKind = computed<StepKind>(() => {
  const i = currentStep.value;
  if (i === totalSteps.value - 1) return "final";
  if (i === 0) return "lose";
  if (i === 1) return "renewal";
  if (i === 2) return "reason";
  return "pause";
});

const primaryLabel = computed(() => {
  switch (stepKind.value) {
    case "lose":
      return "Keep my subscription";
    case "renewal":
      return "Keep access, get reminder";
    case "reason":
      return "Continue";
    case "pause":
      return "Pause my subscription";
    default:
      return "Continue";
  }
});

const linkLabel = computed(() => {
  switch (stepKind.value) {
    case "lose":
    case "renewal":
      return "Continue cancellation";
    case "pause":
      return "No thanks, continue cancellation";
    default:
      return "";
  }
});

function advance() {
  if (currentStep.value < totalSteps.value - 1) currentStep.value += 1;
}

function back() {
  if (currentStep.value > 0) currentStep.value -= 1;
  else emit("close");
}

function onPrimary() {
  switch (stepKind.value) {
    case "lose":
      emit("close");
      break;
    case "renewal":
      showReminderToast();
      break;
    case "reason":
      advance();
      break;
    case "pause":
      doPause();
      break;
  }
}

function showReminderToast() {
  reminderToast.value = true;
  window.setTimeout(() => {
    reminderToast.value = false;
  }, 4000);
}

function contactSupportFromModal() {
  contactSupport();
  showSupportModal.value = false;
}

function continueFromSupport() {
  showSupportModal.value = false;
  advance();
}

async function doPause() {
  if (submitting.value) return;
  submitting.value = true;
  error.value = "";
  try {
    const res: any = await graphClient.request(PauseWebSubscription, {
      input: { durationMonths: pauseDurationEnum(pauseMonths.value) },
    });
    const resumesAt = res?.pauseWebSubscription?.resumesAt;
    resumesOn.value =
      longOrdinalDate(resumesAt) || longOrdinalDate(resumeDate(new Date(), pauseMonths.value)) || "";
    terminal.value = "paused";
  } catch (e) {
    error.value = errorMessage(e);
  } finally {
    submitting.value = false;
  }
}

async function doCancel() {
  if (submitting.value) return;
  // Store-backed subs can't be cancelled in-app — send them to the store, or to
  // support when the source has no self-serve page to open.
  const action = manageAction(props.source, props.managementUrl);
  if (action.kind !== "stripe") {
    cancelNeedsSupport.value = action.kind === "support";
    storeRedirectBody.value = cancelPrompt(action);
    return;
  }
  submitting.value = true;
  error.value = "";
  try {
    await graphClient.request(CancelWebSubscription, {
      input: { reason: cancelReasonPayload(selectedReason.value, otherText.value) },
    });
    terminal.value = "cancelled";
  } catch (e) {
    error.value = errorMessage(e);
  } finally {
    submitting.value = false;
  }
}

function confirmStoreRedirect() {
  if (cancelNeedsSupport.value) {
    contactSupport();
  } else if (props.managementUrl) {
    window.open(props.managementUrl, "_blank", "noopener");
  }
  closeStoreRedirect();
  emit("close");
}

function closeStoreRedirect() {
  storeRedirectBody.value = "";
  cancelNeedsSupport.value = false;
}
</script>
